import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { ScrollText, Edit, Save, AlertTriangle } from 'lucide-react';
import RichTextEditor from './RichTextEditor';
import RichContent from './RichContent';

const CharterContent = ({ editing, editedCharter, setEditedCharter, charter }) => (
  <div className="space-y-4">
    {editing ? (
      <div className="space-y-4">
        <Input
          value={editedCharter.title}
          onChange={(e) => setEditedCharter((prev) => ({ ...prev, title: e.target.value }))}
          placeholder="Titre de la charte"
          className="bg-secondary/50 font-semibold text-lg"
          data-testid="charter-title-input"
        />
        <RichTextEditor
          value={editedCharter.content}
          onChange={(content) => setEditedCharter((prev) => ({ ...prev, content }))}
          rows={14}
          placeholder="Rédige la charte : titres, listes, encadrés…"
          testId="charter-editor"
        />
        <p className="text-xs text-muted-foreground">
          Mise en forme visuelle (gras, titres, listes, liens, encadrés). Bascule sur « HTML » pour coller ton propre code.
        </p>
      </div>
    ) : (
      <RichContent html={charter?.content || ''} className="max-w-none" />
    )}
  </div>
);

const CommunityCharter = ({ showButton = true, inline = false }) => {
  const { api, isAdmin } = useAuth();
  const [charter, setCharter] = useState(null);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editedCharter, setEditedCharter] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCharter();
  }, []);

  const fetchCharter = async () => {
    try {
      const res = await api.get('/charter');
      setCharter(res.data);
      setEditedCharter({ title: res.data.title, content: res.data.content });
    } catch (error) {
      console.error('Error fetching charter:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put('/charter', editedCharter);
      toast.success('Charte mise à jour');
      setCharter({ ...charter, ...editedCharter });
      setEditMode(false);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const charterBody = (
    <CharterContent
      editing={editMode && isAdmin}
      editedCharter={editedCharter}
      setEditedCharter={setEditedCharter}
      charter={charter}
    />
  );

  if (loading) return null;

  if (inline) {
    return (
      <Card className="card-base">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <ScrollText className="w-5 h-5 text-primary" />
              {charter?.title || 'Charte de la communauté'}
            </CardTitle>
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => {
                  if (editMode) handleSave();
                  else setEditMode(true);
                }}
                data-testid="charter-edit-toggle"
              >
                {editMode ? (
                  <>
                    <Save className="w-4 h-4 mr-1" />
                    Enregistrer
                  </>
                ) : (
                  <>
                    <Edit className="w-4 h-4 mr-1" />
                    Modifier
                  </>
                )}
              </Button>
            )}
          </div>
          {charter?.last_updated && (
            <CardDescription>
              Mise à jour le {new Date(charter.last_updated).toLocaleDateString('fr-FR')}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent>
          {charterBody}
        </CardContent>
      </Card>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {showButton && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-xs">
            <ScrollText className="w-4 h-4 mr-1" />
            Charte
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="bg-card border-border max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2 font-heading">
              <ScrollText className="w-5 h-5 text-primary" />
              {charter?.title || 'Charte de la communauté'}
            </DialogTitle>
            {isAdmin && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditMode(!editMode)}
                data-testid="charter-edit-toggle-dialog"
              >
                {editMode ? 'Aperçu' : 'Modifier'}
              </Button>
            )}
          </div>
        </DialogHeader>
        
        {charterBody}

        <DialogFooter>
          {editMode && isAdmin ? (
            <Button onClick={handleSave} className="bg-primary text-primary-foreground" data-testid="charter-save-btn">
              <Save className="w-4 h-4 mr-2" />
              Enregistrer
            </Button>
          ) : (
            <Button variant="outline" onClick={() => setOpen(false)}>
              J'ai compris
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CommunityCharter;
